import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Grid, List, ChevronDown, Star, TrendingUp, Clock, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import ProductCard from '../../components/shared/ProductCard';
import { formatNaira } from '../../utils/formatters';
import { getImageUrl } from '../../utils/imageUtils';
import { ConditionBadge } from '../../components/common/Badge';

const ProductGrid = ({ 
  listings, 
  viewMode = 'grid',
  sortBy = 'newest',
  currentPage = 1,
  onViewModeChange,
  onSortChange,
  onPageChange,
  showControls = true,
  isSearchPage = false,
  hidePagination = false
}) => {
  // Local state for when parent doesn't provide controls
  const [localViewMode, setLocalViewMode] = useState(viewMode);
  const [localSortBy, setLocalSortBy] = useState(sortBy);
  const [localCurrentPage, setLocalCurrentPage] = useState(currentPage);
  const [isMobile, setIsMobile] = useState(false);
  
  const itemsPerPage = 12;

  // Determine which state to use based on props
  const displayViewMode = onViewModeChange !== undefined ? viewMode : localViewMode;
  const displaySortBy = onSortChange !== undefined ? sortBy : localSortBy;
  const displayCurrentPage = onPageChange !== undefined ? currentPage : localCurrentPage;

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate statistics
  const { totalListings, boostedCount, newTodayCount } = useMemo(() => {
    const total = listings.length || 0;
    const boosted = listings.filter(l => l.is_boosted).length;
    const today = new Date();
    const newToday = listings.filter(l => {
      const listingDate = new Date(l.created_at);
      return listingDate.toDateString() === today.toDateString();
    }).length;

    return { totalListings: total, boostedCount: boosted, newTodayCount: newToday };
  }, [listings]);

  // Sort listings with boosted priority
  const sortedListings = useMemo(() => {
    if (!listings || listings.length === 0) return [];

    // Separate boosted and non-boosted listings
    const boostedListings = listings.filter(l => l.is_boosted);
    const regularListings = listings.filter(l => !l.is_boosted);

    // Sort each group separately
    const sortFunction = (a, b) => {
      switch (displaySortBy) {
        case 'price_low_high':
          return (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0);
        case 'price_high_low':
          return (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0);
        case 'rating':
          return (parseFloat(b.seller_rating) || 0) - (parseFloat(a.seller_rating) || 0);
        case 'views':
          return (b.view_count || 0) - (a.view_count || 0);
        case 'relevance':
          // For relevance sorting, prioritize listings with search term in title
          if (isSearchPage) {
            const searchTerm = new URLSearchParams(window.location.search).get('q') || '';
            if (searchTerm) {
              const aHasTerm = a.title?.toLowerCase().includes(searchTerm.toLowerCase());
              const bHasTerm = b.title?.toLowerCase().includes(searchTerm.toLowerCase());
              if (aHasTerm && !bHasTerm) return -1;
              if (!aHasTerm && bHasTerm) return 1;
            }
          }
          // Fall through to newest if no search term
        case 'newest':
        default:
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    };

    const sortedBoosted = [...boostedListings].sort(sortFunction);
    const sortedRegular = [...regularListings].sort(sortFunction);

    // Combine with boosted listings first
    return [...sortedBoosted, ...sortedRegular];
  }, [listings, displaySortBy, isSearchPage]);

  // Pagination
  const { totalPages, currentListings } = useMemo(() => {
    const total = Math.ceil(sortedListings.length / itemsPerPage);
    const startIndex = (displayCurrentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const current = sortedListings.slice(startIndex, endIndex);

    return { totalPages: total, currentListings: current };
  }, [sortedListings, displayCurrentPage]);

  const handlePageChange = useCallback((page) => {
    if (onPageChange) {
      onPageChange(page);
    } else {
      setLocalCurrentPage(page);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [onPageChange]);

  const handleSortChange = useCallback((value) => {
    if (onSortChange) {
      onSortChange(value);
    } else {
      setLocalSortBy(value);
    }
    
    // Reset to first page when sort changes
    if (onPageChange) {
      onPageChange(1);
    } else {
      setLocalCurrentPage(1);
    }
  }, [onSortChange, onPageChange]);

  const handleViewModeChange = useCallback((mode) => {
    if (onViewModeChange) {
      onViewModeChange(mode);
    } else {
      setLocalViewMode(mode);
    }
  }, [onViewModeChange]);

  // For mobile, always use grid view
  const effectiveViewMode = isMobile ? 'grid' : displayViewMode;

  // Empty state
  if (!listings || listings.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-100">
        <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
          <Grid className="w-12 h-12 text-gray-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
        <p className="text-gray-600 max-w-md mx-auto mb-6">
          Try adjusting your search or filter to find what you're looking for.
        </p>
        <Link
          to="/categories"
          className="inline-flex items-center px-6 py-3 bg-[#ff660e] text-white rounded-xl font-medium hover:bg-[#e55a0d] transition-all shadow-sm hover:shadow"
        >
          Browse Categories
        </Link>
      </div>
    );
  }

  // Generate pagination buttons
  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisibleButtons = isMobile ? 3 : 5;

    if (totalPages <= maxVisibleButtons) {
      for (let i = 1; i <= totalPages; i++) {
        buttons.push(i);
      }
    } else {
      if (displayCurrentPage <= (isMobile ? 2 : 3)) {
        for (let i = 1; i <= (isMobile ? 2 : 4); i++) {
          buttons.push(i);
        }
        buttons.push('...');
        buttons.push(totalPages);
      } else if (displayCurrentPage >= totalPages - (isMobile ? 1 : 2)) {
        buttons.push(1);
        buttons.push('...');
        for (let i = totalPages - (isMobile ? 1 : 3); i <= totalPages; i++) {
          buttons.push(i);
        }
      } else {
        buttons.push(1);
        buttons.push('...');
        buttons.push(displayCurrentPage - 1);
        buttons.push(displayCurrentPage);
        buttons.push(displayCurrentPage + 1);
        buttons.push('...');
        buttons.push(totalPages);
      }
    }

    return buttons;
  };

  return (
    <div className="space-y-6">
      {/* Grid Controls - Only show if enabled */}
      {showControls && (
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div className="flex items-center space-x-4 mb-4 md:mb-0">
              {/* View Toggle - Hidden on mobile */}
              <div className="hidden md:flex items-center bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-1 border border-gray-200">
                <button
                  onClick={() => handleViewModeChange('grid')}
                  className={`p-2 rounded-lg transition-all ${effectiveViewMode === 'grid' ? 'bg-white shadow text-[#ff660e]' : 'text-gray-600 hover:text-gray-900'}`}
                  aria-label="Grid view"
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleViewModeChange('list')}
                  className={`p-2 rounded-lg transition-all ${effectiveViewMode === 'list' ? 'bg-white shadow text-[#ff660e]' : 'text-gray-600 hover:text-gray-900'}`}
                  aria-label="List view"
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Sort Dropdown */}
            <div className="relative">
              <select
                value={displaySortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff660e] appearance-none bg-white font-medium text-sm md:text-base w-full md:w-auto"
              >
                <option value="relevance">Most Relevant</option>
                <option value="newest">Newest First</option>
                <option value="price_low_high">Price: Low to High</option>
                <option value="price_high_low">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="views">Most Viewed</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            </div>
          </div>
        </div>
      )}

      {/* Product Grid/List View */}
      {effectiveViewMode === 'grid' ? (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {currentListings.map(listing => (
            <ProductCard
              key={listing.listing_id}
              listing={listing}
              isFavorite={listing.isFavorite}
              onFavoriteClick={listing.onFavoriteClick}
              isMobile={isMobile}
            />
          ))}
        </div>
      ) : (
        // List view only for desktop
        <div className="space-y-4">
          {currentListings.map(listing => (
            <div 
              key={listing.listing_id} 
              className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border ${
                listing.is_boosted 
                  ? 'border-yellow-400 hover:border-yellow-500' 
                  : 'border-gray-100 hover:border-[#ff660e]'
              }`}
            >
              <div className="flex flex-col md:flex-row">
                {/* Image with Boosted Indicator */}
                <div className="md:w-48 md:h-48 w-full h-56 flex-shrink-0 relative">
                  {listing.images && listing.images.length > 0 ? (
                    <img
                      src={getImageUrl(listing.images[0].image_url)}
                      alt={listing.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
                      <div className="text-5xl opacity-20">📦</div>
                    </div>
                  )}
                  {listing.is_boosted && (
                    <div className="absolute top-2 left-2 z-10 px-2 py-1 bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-xs font-medium rounded-full flex items-center shadow-md">
                      <Zap className="w-3 h-3 mr-1" />
                      
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 hover:text-[#ff660e] transition-colors">
                        <Link to={`/listing/${listing.listing_id}`}>
                          {listing.title}
                        </Link>
                      </h3>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className="text-sm text-gray-500 bg-gradient-to-r from-gray-50 to-gray-100 px-3 py-1.5 rounded-lg border border-gray-200">
                          {listing.category_name}
                        </span>
                        {/* UPDATED: Condition Badge */}
                        <ConditionBadge 
                          condition={listing.condition} 
                          size="small"
                        />
                      </div>
                      
                      <p className="text-gray-600 mb-4 line-clamp-2">
                        {listing.description}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Star className="w-4 h-4 text-yellow-400 mr-1 fill-current" />
                          <span>{listing.seller_rating || 'No ratings'}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="mr-1">📍</span>
                          <span>{listing.state_name || 'Nationwide'}</span>
                        </div>
                        
                        <div className="hidden md:flex items-center">
                          <span className="mr-1">👁️</span>
                          <span>{listing.view_count || 0} views</span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 md:mt-0 md:ml-6 md:text-right">
                      <div className={`text-xl md:text-2xl font-bold mb-2 ${
                        listing.is_boosted ? 'text-yellow-600' : 'text-gray-900'
                      }`}>
                        {formatNaira(listing.price)}
                      </div>
                      {listing.is_negotiable === 1 && (
                        <div className="text-sm text-purple-600 mb-4">Price negotiable</div>
                      )}
                      <Link
                        to={`/listing/${listing.listing_id}`}
                        className="inline-flex items-center px-4 md:px-6 py-2 bg-[#ff660e] text-white rounded-xl font-medium hover:bg-[#e55a0d] transition-all shadow-sm hover:shadow"
                      >
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination - Only show if enabled and multiple pages */}
      {!hidePagination && totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between pt-6 border-t border-gray-200">
          <div className="text-sm text-gray-600 mb-4 sm:mb-0">
            Showing <span className="font-medium">{currentListings.length}</span> of{' '}
            <span className="font-medium">{sortedListings.length}</span> products
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(displayCurrentPage - 1)}
              disabled={displayCurrentPage === 1}
              className="px-3 sm:px-4 py-2 border border-gray-300 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors font-medium text-sm sm:text-base"
            >
              Previous
            </button>
            
            {renderPaginationButtons().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl font-medium transition-all text-sm sm:text-base ${
                    displayCurrentPage === page
                      ? 'bg-[#ff660e] text-white shadow'
                      : 'border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              )
            ))}
            
            <button
              onClick={() => handlePageChange(displayCurrentPage + 1)}
              disabled={displayCurrentPage === totalPages}
              className="px-3 sm:px-4 py-2 border border-gray-300 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors font-medium text-sm sm:text-base"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductGrid;